import { forwardRef, useRef, useState } from 'react';
import styled from 'styled-components/macro';
import BuildingWrapper from './building-wrapper';
import bgImg from '../assets/buildings/ksb-team/bg.jpg';
import beck from '../assets/buildings/ksb-team/beck.png';
import beckOverlay from '../assets/buildings/ksb-team/beck-overlay.png';
import bollmann from '../assets/buildings/ksb-team/bollmann.png';
import bollmannOverlay from '../assets/buildings/ksb-team/bollmann-overlay.png';
import terock from '../assets/buildings/ksb-team/terock.png';
import terockOverlay from '../assets/buildings/ksb-team/terock-overlay.png';
import manss from '../assets/buildings/ksb-team/manss.png';
import manssOverlay from '../assets/buildings/ksb-team/manss-overlay.png';
import schmidt from '../assets/buildings/ksb-team/schmidt.png';
import schmidtOverlay from '../assets/buildings/ksb-team/schmidt-overlay.png';
import useTranslations from './utils/language-provider';
import { BuildingType } from '../config/buildings';
import Carousel from './lib/react-carousel3d';
import { Anchor } from './ui/button';
import { ReactComponent as ArrowRight } from '../assets/arrow-right.svg';
import { Language } from '../interfaces/language';

type KSBTeamProps = {}

const Background = styled.div`
    position: relative;
    display: flex;
    height: calc(100% - 75px);
    background: url(${bgImg});
    background-size: contain;
    background-repeat: no-repeat;
    align-items: center;
`

const TextBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 10px 28% 10px 10px;
    background: rgba(255,255,255,.8);
    color: ${props => props.theme.colors.primaryDark};
    font-size: ${props => props.theme.typography.xs};

    @media ${props => props.theme.screens.md} {
        font-size: ${props => props.theme.typography.md};
    }

    @media ${props => props.theme.screens.lg} {
        padding: 30px 100px 30px 30px;
        margin: 0 0 0 80px;
    }

    img {
        position: absolute;
        right: 30px;
        bottom: 0;
        height: 95%;

        @media ${props => props.theme.screens.lg} {
            display: none;
        }
    }
`

const TextBoxTitle = styled.div`
    font-weight: 700;

    @media ${props => props.theme.screens.md} {
        font-size: ${props => props.theme.typography.xl};
        font-weight: 900;
        text-transform: uppercase;
    }
`

const CarouselWrapper = styled.div`
    position: absolute;
    bottom: 0px;
    right: 0px;
    flex-shrink: 0;

    @media ${props => props.theme.screens.lg} {
        position: relative;
        width: 48%;
        height: 100%;
    }
`

const StyledCarousel = styled(Carousel)`
    display: none;

    @media ${props => props.theme.screens.lg} {
       display: block;
    }
`

const CarouselImage = styled.img`
    height: 95%;
`

const OverlayImage = styled.img<{active: boolean}>`
    pointer-events: none;
    position: absolute;
    top: 0;
    left: 0;
    height: 95%;
    transition: all 300;
    opacity: ${props => props.active ? '0' : '.5'};
`

const CarouselButton = styled.button<{direction: string}>`
    margin: 0 10px 20px;
    z-index: ${props => props.theme.zIndex.modal};
    width: 40px;
    height: 40px;
    color: ${props => props.theme.colors.primary};
    border: none;
    background: ${props => props.theme.colors.white};
    padding: 0;
    padding-top: 3px;
    padding-left: ${props => props.direction === 'prev' ? '0' : '3px'};
    border-radius: 100%;
    opacity: .5;
    transition: all 300ms;

    @media ${props => props.theme.screens.lg} {
        position: absolute;
        bottom: 220px;
        left: ${props => props.direction === 'prev' ? '150px' : '430px'};
        margin: 0;
    }

    &:hover {
        opacity: 1;
        transform: scale(1.2);
    }

    svg {
        width: 13px;
        transform: ${props => props.direction === 'prev' ? 'rotate(180deg)' : 'none'};
    }
`

const getItems = (lang: Language) => {
    const members = lang.buildings[BuildingType.KSB_TEAM].members;
    return [
        {
            img: bollmann,
            overlay: bollmannOverlay,
            width: '187px',
            height: '600px',
            title: members.bollmann.title,
            href: members.bollmann.href,
            content: members.bollmann.content,
        },
        {
            img: terock,
            overlay: terockOverlay,
            width: '245px',
            height: '600px',
            title: members.terock.title,
            href: members.terock.href,
            content: members.terock.content,
        },
        {
            img: manss,
            overlay: manssOverlay,
            width: '207px',
            height: '600px',
            title: members.manss.title,
            href: members.manss.href,
            content: members.manss.content,
        },
        {
            img: schmidt,
            overlay: schmidtOverlay,
            width: '185px',
            height: '600px',
            title: members.schmidt.title,
            href: members.schmidt.href,
            content: members.schmidt.content,
        },
        {
            img: beck,
            overlay: beckOverlay,
            width: '171px',
            height: '600px',
            title: members.beck.title,
            href: members.beck.href,
            content: members.beck.content,
        },
    ];
};

const KSBTeam = forwardRef<HTMLDivElement, KSBTeamProps>((props, ref) => {
    const translations = useTranslations();
    const [activeIndex, setActiveIndex] = useState(0);

    const items = getItems(translations);
    const selectedItem = items[activeIndex];
    const carouselRef = useRef<any>();

    const handlePrev = () => {
        if (carouselRef?.current) {
            carouselRef.current.gotoIndex(activeIndex === (items.length - 1) ? 0 : (activeIndex + 1));
        }
    }

    const handleNext = () => {
        if (carouselRef?.current) {
            carouselRef.current.gotoIndex(activeIndex === 0 ? (items.length - 1) : (activeIndex - 1));
        }
    }

    return (
        <BuildingWrapper ref={ref}>
            <Background>
                <TextBox>
                    <TextBoxTitle>
                        {selectedItem.title}
                    </TextBoxTitle>

                    <p>
                        {selectedItem.content}
                    </p>

                    <img src={selectedItem.img} alt="" />

                    {!!selectedItem.href && <Anchor secondary href={selectedItem.href}>
                        {translations.buildings[BuildingType.KSB_TEAM].ctaText}
                    </Anchor>}
                </TextBox>
                <CarouselWrapper>
                    <StyledCarousel
                        height="10%"
                        width="600px"
                        yOrigin={100}
                        xOrigin={550}
                        yRadius={50}
                        // xRadius={350}
                        autoPlay={false}
                        onChange={index => setActiveIndex(index)}
                        innerRef={(el: any) => carouselRef.current = el}
                    >
                        {items.map((item, index) => {
                            const isLeft = (activeIndex + 1 === index || (activeIndex + 1 > items.length && index === 0));
                            const isRight = (activeIndex - 1 === index || (activeIndex - 1 < 0 && index === items.length - 1));

                            return (
                                <div
                                    key={item.img}
                                    style={{
                                        width: item.width,
                                        height: item.height,
                                        transition: 'margin 500ms, transform 200ms, left 200ms, top 200ms',
                                        marginTop: index === activeIndex ? '30px' : 0,
                                        left: isLeft ? '-30px' : isRight ? '30px' : 0,
                                        top: isLeft || isRight ? '20px' : 0,
                                    }}
                                >
                                    <CarouselImage alt="" src={item.img} />
                                    <OverlayImage alt="" src={item.overlay} active={index === activeIndex} />
                                </div>
                            )
                        })}
                    </StyledCarousel>

                    <CarouselButton direction="prev" onClick={handlePrev}>
                        <ArrowRight />
                    </CarouselButton>

                    <CarouselButton direction="next" onClick={handleNext}>
                        <ArrowRight />
                    </CarouselButton>
                </CarouselWrapper>
            </Background>
        </BuildingWrapper>
    )
})

export default KSBTeam;
